import { trapFocus, removeTrapFocus, lockScroll, unlockScroll, closeDrawer, setDrawerOpenAccessibility } from '@theme/theme-global';

if (!customElements.get('header-mobile-menu')) {
  class HeaderMobileMenu extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this._drawerBody = this.querySelector('.drawer-inner');
      this._closeBtn = this.querySelector('.drawer-close-btn');
      this._focusTarget = this._drawerBody || this._closeBtn;

      this._handleKeyup = (evt) => {
        if (evt.code === 'Escape') this.close();
      };
      this.addEventListener('keyup', this._handleKeyup);

      this.setMobileMenuIconAccessibility();
      this.drawerCloseInit();
    }
    setMobileMenuIconAccessibility() {
      const drawerIcon = document.querySelector('#MobileMenuDrawerIcon');
      if (!drawerIcon) return;

      drawerIcon.setAttribute('role', 'button');
      drawerIcon.setAttribute('aria-haspopup', 'dialog');

      drawerIcon.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(drawerIcon);
      });

      drawerIcon.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(drawerIcon);
        }
      });
    }

    setTriggersExpanded(expanded) {
      document
        .querySelectorAll('#MobileMenuDrawerIcon, button[is="mobile-menu-toggle"]')
        .forEach((trigger) => {
          trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      setDrawerOpenAccessibility(this);
      window.setTimeout(() => {
        this.setAttribute('open', '');
        this.setAttribute('active', '');
      });

      this.setTriggersExpanded(true);

      this.addEventListener(
        'transitionend',
        () => {
          trapFocus(this, this._focusTarget);
        },
        { once: true }
      );

      lockScroll();
    }

    drawerCloseInit() {
      this.querySelectorAll('[is="close-drawer"]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          closeDrawer(event.target);
          removeTrapFocus(this.activeElement);
          unlockScroll();
          this.setTriggersExpanded(false);
          this.querySelectorAll('details[is=mobile-menu-drawer-details]').forEach((menu) => {
            menu.onCloseButtonClick();
          });
          this.querySelectorAll('details[is=accordion-details]').forEach((menu) => {
            menu.close();
          });
        });
      });
    }

    close() {
      closeDrawer(this);
      removeTrapFocus(this.activeElement);
      unlockScroll();
      this.setTriggersExpanded(false);
      this.querySelectorAll('details[is=mobile-menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
      this.querySelectorAll('details[is=accordion-details]').forEach((menu) => {
        menu.close();
      });
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

  }
  customElements.define('header-mobile-menu', HeaderMobileMenu);
}

if (!customElements.get('mobile-menu-toggle')) {
  class MobileMenuToggle extends HTMLButtonElement {
    connectedCallback() {
      this.setAttribute('aria-haspopup', 'dialog');
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    onClick = (event) => {
      event.preventDefault();
      this.openMenu();
    };

    onKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openMenu();
      }
    };

    openMenu() {
      const menu = document.getElementById('HeaderMobileMenu');
      if (menu && typeof menu.open === 'function') {
        menu.open(this);
      }
    }
  }

  customElements.define('mobile-menu-toggle', MobileMenuToggle, { extends: 'button' });
}

class MobileMenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
    }
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('[data-back-button]');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent?.classList.add('active');
      this.classList.add('active');
      this.summary?.setAttribute('aria-expanded', 'true');
    }, 100);
  }

  onCloseButtonClick() {
    this.parent?.classList.remove('active');
    this.classList.remove('active');
    this.summary?.setAttribute('aria-expanded', 'false');

    this.closeAnimation();
    this.querySelectorAll('details[is=accordion-details]').forEach((menu) => {
      menu.close();
    });
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('mobile-menu-drawer-details', MobileMenuDrawerDetails, { extends: 'details' });
